<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import ResultCard from '@/components/search/ResultCard.vue';
import {
  clearRecentSearches,
  clearRecentViews,
  readRecentSearches,
  readRecentViews,
  recordSearch,
} from '@/lib/recent';

/**
 * The full-screen search sheet. It is shown by the `#search` hash rather than a
 * route of its own, so the page underneath stays mounted behind the slide-up and
 * the browser's (or phone's) back button is what closes it.
 */
const router = useRouter();

const query = ref('');
const searches = ref(readRecentSearches());
const views = ref(readRecentViews());
const field = ref<HTMLInputElement | null>(null);

onMounted(() => field.value?.focus());

/** Closing = removing the hash, which is one step back in history — unless the
    sheet was the landing page (a shared `/#search` link), where back would leave
    the site instead of the sheet. */
const close = (): void => {
  if (window.history.state?.back != null) router.back();
  else void router.replace({ hash: '' });
};

function submit(): void {
  const q = query.value.trim();
  if (!q) return;
  recordSearch(q);
  // Replace the sheet's history entry: back from the results skips the sheet.
  void router.replace({ name: 'search', query: { q } });
}

function searchFor(term: string): void {
  recordSearch(term);
  void router.replace({ name: 'search', query: { q: term } });
}

/** The ↖ affordance: put the term in the field to edit it, don't run it yet. */
function fill(term: string): void {
  query.value = term;
  field.value?.focus();
}

function onClearSearches(): void {
  clearRecentSearches();
  searches.value = [];
}

function onClearViews(): void {
  clearRecentViews();
  views.value = [];
}
</script>

<template>
  <div class="overlay" role="dialog" aria-label="Search services">
    <header class="overlay__bar">
      <button class="overlay__back" type="button" aria-label="Back" @click="close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M19 12H5M11.5 5.5 5 12l6.5 6.5" />
        </svg>
      </button>
      <form class="overlay__form" role="search" @submit.prevent="submit">
        <label class="sr-only" for="overlay-q">Search services</label>
        <input
          id="overlay-q"
          ref="field"
          v-model="query"
          type="search"
          autocomplete="off"
          placeholder="Search services"
          enterkeyhint="search"
        />
      </form>
    </header>

    <div class="overlay__body">
      <section v-if="searches.length" class="block">
        <header class="block__head">
          <h2>Recent searches</h2>
          <button class="block__clear" type="button" @click="onClearSearches">Clear history</button>
        </header>
        <ul class="terms">
          <li v-for="term in searches" :key="term">
            <button class="terms__go" type="button" @click="searchFor(term)">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="8.4" />
                <path d="M12 7.6V12l3 2.4" />
              </svg>
              <span>{{ term }}</span>
            </button>
            <button
              class="terms__fill"
              type="button"
              :aria-label="`Edit search “${term}”`"
              @click="fill(term)"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M7 17 17 7M9 7h8v8" />
              </svg>
            </button>
          </li>
        </ul>
      </section>

      <section v-if="views.length" class="block">
        <header class="block__head">
          <h2>Recently viewed</h2>
          <button class="block__clear" type="button" @click="onClearViews">Clear</button>
        </header>
        <div class="rail">
          <ResultCard v-for="view in views" :key="view.result.id" :result="view.result" />
        </div>
      </section>

      <p v-if="!searches.length && !views.length" class="overlay__empty muted">
        Search a trade or describe the job — "leaky tap", "deep clean", "maths tutor".
      </p>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  padding-top: env(safe-area-inset-top);
}

.overlay__bar {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: none;
  min-height: var(--nav-h);
  padding: 0 var(--gap) 0 0.4rem;
  border-bottom: 1px solid var(--border);
}

.overlay__back {
  display: grid;
  place-items: center;
  flex: none;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text);
  cursor: pointer;
}
.overlay__back:hover { background: var(--surface-2); }

.overlay__form { flex: 1; min-width: 0; }
.overlay__form input {
  width: 100%;
  min-height: 44px;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 1.05rem;
  color: var(--text);
}
.overlay__form input::placeholder { color: var(--muted); }

.overlay__body {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem var(--gap) calc(1.5rem + env(safe-area-inset-bottom));
}

.block { margin-top: 1.1rem; }
.block__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
}
.block__head h2 { margin: 0; font-size: 1.15rem; font-weight: 650; }

.block__clear {
  appearance: none;
  border: 0;
  background: none;
  padding: 0.25rem 0;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 550;
  color: var(--accent);
  cursor: pointer;
}
.block__clear:hover { text-decoration: underline; }

/* ── The term rows ───────────────────────────────────────────────────────── */
.terms { margin: 0; padding: 0; list-style: none; }
.terms li { display: flex; align-items: center; border-bottom: 1px solid var(--border); }

.terms__go {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex: 1;
  min-width: 0;
  padding: 0.8rem 0.15rem;
  border: 0;
  background: none;
  font: inherit;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.terms__go svg { flex: none; color: var(--muted); }
.terms__go span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.terms__fill {
  display: grid;
  place-items: center;
  flex: none;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--muted);
  cursor: pointer;
}
.terms__fill:hover { color: var(--text); background: var(--surface-2); }

/* ── The viewed rail (same pattern as the home feed) ─────────────────────── */
.rail {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min(240px, 64vw);
  gap: 0.9rem;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  margin-inline: calc(-1 * var(--gap));
  padding: 0.4rem var(--gap) 0.5rem;
  scroll-padding-inline: var(--gap);
  scrollbar-width: none;
}
.rail::-webkit-scrollbar { display: none; }
.rail > * { scroll-snap-align: start; min-width: 0; }

.overlay__empty { margin: 2.5rem auto 0; max-width: 34ch; text-align: center; }

/* Wide screens get a column, not a wall. */
@media (min-width: 720px) {
  .overlay__bar { padding-inline: max(0.4rem, calc((100% - 640px) / 2)) max(var(--gap), calc((100% - 640px) / 2)); }
  .overlay__body { padding-inline: max(var(--gap), calc((100% - 640px) / 2)); }
}
</style>
